import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { inviteService } from '../services/inviteService';
import { supabase } from '../lib/supabase';

const CompanyContext = createContext({});

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const { profile, user } = useAuth();
  const [userCompanies, setUserCompanies] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch full company details including logo, address, phone, etc.
  const fetchFullCompanyDetails = async (companyId) => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('logo_url, address_line1, address_line2, city, state, postcode, country, phone, contact_email, website, timezone, business_hours')
        .eq('id', companyId)
        .single();

      if (error) {
        console.warn('Could not fetch full company details:', error.message);
        return {};
      }

      return data || {};
    } catch (err) {
      console.warn('Error fetching company details:', err);
      return {};
    }
  };

  // Load all companies the user belongs to
  const loadCompanies = useCallback(async () => {
    // Use user instead of isAuthenticated (which doesn't exist in AuthContext)
    if (!profile?.id || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the new RPC function to get all user companies
      const result = await inviteService.getUserCompanies();
      const companies = result.companies || [];

      setUserCompanies(companies);

      // Determine which company to select
      const savedCompanyId = localStorage.getItem('mervo_current_company');

      if (companies.length > 0) {
        // Try to restore previously selected company
        let selectedCompany = null;

        if (savedCompanyId) {
          selectedCompany = companies.find(c => c.company_id === savedCompanyId);
        }

        // If no saved company or saved company not found, use first one
        if (!selectedCompany) {
          selectedCompany = companies[0];
        }

        // Fetch full company details including logo, address, etc.
        const fullCompanyData = await fetchFullCompanyDetails(selectedCompany.company_id);

        setCurrentCompany({
          id: selectedCompany.company_id,
          company_name: selectedCompany.company_name,
          company_tag: selectedCompany.company_tag,
          role: selectedCompany.role,
          company_alias: selectedCompany.company_alias,
          // Include additional company details for invoices
          ...fullCompanyData
        });

        localStorage.setItem('mervo_current_company', selectedCompany.company_id);
      } else {
        setCurrentCompany(null);
      }
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [profile?.id, user]);

  // Load companies on auth change
  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Switch to a different company
  const switchCompany = useCallback(async (companyId) => {
    const company = userCompanies.find(c => c.company_id === companyId);

    if (!company) {
      console.error('Company not found:', companyId);
      return false;
    }

    // Fetch full company details including logo, address, etc.
    const fullCompanyData = await fetchFullCompanyDetails(companyId);

    setCurrentCompany({
      id: company.company_id,
      company_name: company.company_name,
      company_tag: company.company_tag,
      role: company.role,
      company_alias: company.company_alias,
      // Include additional company details for invoices
      ...fullCompanyData
    });

    localStorage.setItem('mervo_current_company', company.company_id);

    return true;
  }, [userCompanies]);

  // Refresh companies (called after accepting an invite)
  const refreshCompanies = useCallback(async () => {
    await loadCompanies();
  }, [loadCompanies]);

  // Get the appropriate dashboard route based on current company role
  const getDashboardRoute = useCallback(() => {
    if (!currentCompany) return '/';

    if (currentCompany.role === 'contractor') {
      return '/contractor/dashboard';
    }

    return '/corporate/dashboard';
  }, [currentCompany]);

  // Check if user has a specific role in current company
  const hasRole = useCallback((roles) => {
    if (!currentCompany) return false;

    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(currentCompany.role);
  }, [currentCompany]);

  // Check if user can perform management actions (create users, etc.)
  const canManageWorkforce = useCallback(() => {
    if (!currentCompany) return false;

    const managementRoles = [
      'primary_owner',
      'owner',
      'executive',
      'general_manager',
      'department_head',
      'manager',
      'supervisor'
    ];

    return managementRoles.includes(currentCompany.role);
  }, [currentCompany]);

  // Compute effective account type based on current company role
  // This allows users to have different portal access per company
  const effectiveAccountType = useMemo(() => {
    // Superadmins always stay superadmin regardless of company role
    if (profile?.account_type === 'superadmin') {
      return 'superadmin';
    }

    // No company selected - fall back to global account_type
    if (!currentCompany) {
      return profile?.account_type || 'contractor';
    }

    // Derive from current company role
    // contractor role -> 'contractor' portal
    // any other role -> 'corporate' portal
    return currentCompany.role === 'contractor' ? 'contractor' : 'corporate';
  }, [profile?.account_type, currentCompany]);

  const value = {
    // State
    userCompanies,
    currentCompany,
    loading,
    error,

    // Actions
    switchCompany,
    refreshCompanies,

    // Helpers
    getDashboardRoute,
    hasRole,
    canManageWorkforce,

    // Computed
    hasMultipleCompanies: userCompanies.length > 1,
    companyCount: userCompanies.length,
    effectiveAccountType
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export default CompanyContext;
